import React from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'react-apollo'
import gql from 'graphql-tag'
import { default as fragments } from '../../fragments'

import {
  Input,
  Spinner,
  InfoDisplay,
  SpanWithData,
  Select
} from '../'

export class ContactResponsibilities extends React.Component {
  static propTypes = {
    contactId: PropTypes.string.isRequired,
    title: PropTypes.string,
    data: PropTypes.object.isRequired
  }

  static defaultProps = {
    title: 'Role & responsibilities'
  }

  constructor(props) {
    super(props)

    this.state = {
      editResponsibilities: false,
      role: null,
      position: null,
      division: null,
      payrollcycle: null,
      saveInProgress: false
    }
  }

  goEditResponsibilityInfo = () => {
    let contact = this.props.data.contact

    this.setState({
      editResponsibilities: true,
      role: contact.role,
      position: contact.position,
      division: contact.division ? contact.division.id : null,
      payrollcycle: contact.payrollcycle ? contact.payrollcycle.id : null
    })
  }

  goReadOnly = () => {
    this.setState({
      editResponsibilities: false,
      saveInProgress: false
    })
  }

  noChangesToSave = () => {
    let contact = this.props.data.contact

    return contact.role === this.state.role &&
      contact.position === this.state.position &&
      this.state.division === '' &&
      this.state.payrollcycles === ''
  }

  handleResponsibilityInfoUpdate = () => {
    this.setState({ saveInProgress: true })

    let contact = this.props.data.contact
    let input = {
      id: this.props.data.contact.id
    }

    if (contact.role !== this.state.role)
      input.role = this.state.role

    if (contact.position !== this.state.position)
      input.position = this.state.position

    if (this.state.division !== null)
      input.division = this.state.division

    if (this.state.payrollcycle !== null)
      input.payrollcycle = this.state.payrollcycle

    if (Object.keys(input).length > 1) {  // check whether fields other than just 'id' exist on obj
      this.props.updateContact({ variables: { input } })
        .then(this.goReadOnly)
        .catch(() => this.setState({ saveInProgress: false }))
    } else {
      this.setState({ saveInProgress: false })
    }
  }

  render() {
    if (this.props.data.loading) {
      return <Spinner className='slds-is-relative' size='small' />
    }

    if (this.props.data.error) {
      return <h1>Oh dear! We have an issue</h1>
    }

    const contact = this.props.data.contact
    const contactEmployer = this.props.data.contact.employer

    let payrollCycles = []
    if (contactEmployer.payrollcycles) {
      contactEmployer.payrollcycles.edges.forEach(({ node: payrollCycle }) => {
        payrollCycles.push({
          value: payrollCycle.id,
          label: payrollCycle.name
        })
      })
    }

    let divisions = []
    if (contactEmployer.divisions) {
      contactEmployer.divisions.edges.forEach(({ node: division }) => {
        divisions.push({
          value: division.id,
          label: division.name
        })
      })
    }

    let responsibilityInfoRows = (
      <tbody>
        <tr>
          <th className='slds-size_1-of-4 slds-theme--shade slds-text-heading--label slds-cell-wrap'>Role</th>
          <td>{
            this.state.editResponsibilities
              ? <Input
                  id='role'
                  placeholder='Role'
                  value={this.state.role || ''}
                  onChange={(e) => this.setState({ role: e.target.value })}
                 />
              : contact.role
            }
          </td>
        </tr>
        <tr>
          <th className='slds-size_1-of-4 slds-theme--shade slds-text-heading--label slds-cell-wrap'>Position</th>
          <td>{
            this.state.editResponsibilities
              ? <Input
                  id='position'
                  placeholder='Position'
                  value={this.state.position || ''}
                  onChange={(e) => this.setState({ position: e.target.value })}
                 />
              : contact.position
            }
          </td>
        </tr>
        <tr>
          <th className='slds-size_1-of-4 slds-theme--shade slds-text-heading--label slds-cell-wrap'>Division</th>
          <td>{
            this.state.editResponsibilities
              ? <Select
                  id='division'
                  default=''
                  placeholder='Choose division'
                  value={this.state.division}
                  options={divisions}
                  onChange={(e) => this.setState({ division: e.target.value })}
                />
              : contact.division ? contact.division.name : 'None'
            }
          </td>
        </tr>
        <tr>
          <th className='slds-size_1-of-4 slds-theme--shade slds-text-heading--label slds-cell-wrap'>Payroll cycle</th>
          <td>{
            this.state.editResponsibilities
              ? <Select
                  id='payrollcycle'
                  default=''
                  placeholder='Choose payroll cycle'
                  value={this.state.payrollcycle}
                  options={payrollCycles}
                  onChange={(e) => this.setState({ payrollcycle: e.target.value })}
                />
              : contact.payrollcycle
                ? <span>
                    {contact.payrollcycle.name} - <SpanWithData
                      forField='PayrollCycleCycle'
                      searchKey={contact.payrollcycle.cycle}
                    />
                  </span>
                : 'None'
            }
          </td>
        </tr>
      </tbody>
    )

    return (
      <InfoDisplay
        title={
          <b>
            {this.props.title}
          </b>
        }
        rows={responsibilityInfoRows}
        edit={this.state.editResponsibilities}
        goBack={this.goBack}
        onCancelClick={this.goReadOnly}
        onSaveClick={this.handleResponsibilityInfoUpdate}
        onEditClick={this.goEditResponsibilityInfo}
        titleBold={false}
        saveDisabled={this.noChangesToSave() || this.state.saveInProgress}
        saveButtonLabel={this.state.saveInProgress}
        className='slds-m-bottom_x-large'
      />
    )
  }
}

export const getContactResponsibilities = gql`query($id: ID!) {
    contact(id: $id) {
      ...Contact
    }
  }
  ${fragments.contact}
`

export const updateContact = gql`
  mutation updateContact($input: ContactMutationInput!) {
    contactMutate(input: $input) {
      contact {
        ...Contact
      }
    }
  }
  ${fragments.contact}
`

export const withData =
  graphql(getContactResponsibilities, {
    options: ({ contactId }) => ({
      variables: {
        id: contactId
      }
    })
  })

export const withContactMutate =
  graphql(updateContact, {
    name: 'updateContact'
  })

export default withData(withContactMutate(ContactResponsibilities))



// WEBPACK FOOTER //
// ./src/components/contact-responsibilities/index.js